// src/lib/memoryVerseNotifications.js
// ─────────────────────────────────────────────────────────────────────────────
//  Daily Memory Verse reminder via @capacitor/local-notifications.
//  Native (Android/iOS) only — on the web every call is a safe no-op.
//
//  One repeating notification (fixed id) fires every day at the chosen time.
//  It is re-scheduled whenever the app opens so the body names the verse that
//  is currently due. Tapping it opens /memory-verse (see MemoryVerseReminder).
// ─────────────────────────────────────────────────────────────────────────────
import { Capacitor } from '@capacitor/core';

const NOTIFICATION_ID = 7301;
const CHANNEL_ID      = 'memory-verse';

export const remindersSupported = () => Capacitor.isNativePlatform();

async function getPlugin() {
  const mod = await import('@capacitor/local-notifications');
  return { LN: mod.LocalNotifications };
}

/** Asks the OS for notification permission. Returns 'granted' | 'denied' | 'unsupported'. */
export async function requestReminderPermission() {
  if (!remindersSupported()) return 'unsupported';
  try {
    const { LN } = await getPlugin();
    let perm = await LN.checkPermissions();
    if (perm.display !== 'granted') perm = await LN.requestPermissions();
    return perm.display === 'granted' ? 'granted' : 'denied';
  } catch (err) {
    console.warn('[memoryVerse] permission request failed', err);
    return 'denied';
  }
}

/** (Re)schedules the daily reminder. Returns true when it was scheduled. */
export async function scheduleDailyReminder({ hour, minute, body }) {
  if (!remindersSupported()) return false;
  try {
    const { LN } = await getPlugin();
    const perm = await LN.checkPermissions();
    if (perm.display !== 'granted') return false;
    try {
      await LN.createChannel({
        id: CHANNEL_ID, name: 'Scripture Memory',
        description: 'Daily Scripture Memory reminder', importance: 3,
      });
    } catch { /* createChannel is Android-only */ }
    await LN.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
    await LN.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: 'Scripture Memory 📖',
        body,
        channelId: CHANNEL_ID,
        schedule: { on: { hour, minute }, allowWhileIdle: true },
        extra: { route: '/memory-verse' },
      }],
    });
    return true;
  } catch (err) {
    console.warn('[memoryVerse] schedule failed', err);
    return false;
  }
}

const TEST_ID = 7302;

/** Android 12+: are exact alarms allowed? Returns 'granted' | 'denied' | 'unknown'. */
export async function exactAlarmStatus() {
  if (!remindersSupported()) return 'unknown';
  try {
    const { LN } = await getPlugin();
    const res = await LN.checkExactNotificationSetting();
    return res.exact_alarm === 'granted' ? 'granted' : 'denied';
  } catch { return 'unknown'; }
}

/** Opens the Android "Alarms & reminders" screen for this app. */
export async function openExactAlarmSettings() {
  try {
    const { LN } = await getPlugin();
    await LN.changeExactNotificationSetting();
  } catch { /* not available below Android 12 */ }
}

/** Fires a test notification in about 5 s. Returns 'sent' | 'no-permission' | 'unsupported' | 'error'. */
export async function sendTestReminder() {
  if (!remindersSupported()) return 'unsupported';
  try {
    const { LN } = await getPlugin();
    const perm = await LN.checkPermissions();
    if (perm.display !== 'granted') return 'no-permission';
    try {
      await LN.createChannel({
        id: CHANNEL_ID, name: 'Scripture Memory',
        description: 'Daily Scripture Memory reminder', importance: 3,
      });
    } catch { /* createChannel is Android-only */ }
    await LN.schedule({
      notifications: [{
        id: TEST_ID,
        title: 'Scripture Memory 📖',
        body: 'Test reminder. Notifications are working.',
        channelId: CHANNEL_ID,
        schedule: { at: new Date(Date.now() + 5000), allowWhileIdle: true },
        extra: { route: '/memory-verse' },
      }],
    });
    return 'sent';
  } catch (err) {
    console.warn('[memoryVerse] test notification failed', err);
    return 'error';
  }
}

export async function cancelDailyReminder() {
  if (!remindersSupported()) return;
  try {
    const { LN } = await getPlugin();
    await LN.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
  } catch { /* nothing scheduled / plugin unavailable */ }
}

/** Calls handler() when the user taps the reminder. Returns an unsubscribe function. */
export async function onReminderTapped(handler) {
  if (!remindersSupported()) return () => {};
  try {
    const { LN } = await getPlugin();
    const sub = await LN.addListener('localNotificationActionPerformed', (event) => {
      if (event?.notification?.id === NOTIFICATION_ID || event?.notification?.id === TEST_ID) handler();
    });
    return () => { sub.remove(); };
  } catch {
    return () => {};
  }
}